import { TEST_IDS } from '@va/constants';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { memo, useMemo } from 'react';
import { CalendarDate, CalendarObj, PanelView } from '../types';

type HeaderProps = {
  panel: PanelView;
  calendar: CalendarObj;
};

export const Header = memo(({ panel, calendar }: HeaderProps) => {
  const showRightChevron = useMemo(() => {
    if (panel === PanelView.Month) return false;
    if (panel === PanelView.Calendar) {
      const lastDisplayedDate = calendar.date().at(-1) as CalendarDate;
      return dayjs().isSameOrAfter(lastDisplayedDate, 'day');
    }
    if (panel === PanelView.Year) {
      const lastDisplayedYear = calendar.years().at(-1) as number;
      return lastDisplayedYear < dayjs().year();
    }
  }, [calendar, panel]);

  return (
    <div
      data-testid={TEST_IDS.generic.datePicker.header}
      className='flex justify-between items-center px-2 py-1.5 rounded-md border border-black/10 dark:border-primary-700'
    >
      <div className='shrink-0 contents'>
        <span
          className={classNames('inline-flex rounded-full', {
            block: panel === PanelView.Calendar || panel === PanelView.Year,
            'visibility-hidden': panel === PanelView.Month,
          })}
        >
          <button
            type='button'
            className='btn-class relative p-1.5 rounded-full bg-white transition-colors border border-transparent hover:bg-white-snow focus:bg-primary-50 focus:border-primary focus:ring-1 focus:ring-primary focus:ring-opacity-10 focus:outline-none '
            onClick={() => (panel === PanelView.Calendar ? calendar.onPrevious() : calendar.onPreviousYear())}
            data-testid={
              panel === PanelView.Calendar
                ? TEST_IDS.generic.datePicker.prevMonthArrow
                : TEST_IDS.generic.datePicker.prevYearArrow
            }
          >
            <ArrowButtonIcon orientation='left' doubled={panel === PanelView.Year} />
          </button>
        </span>
      </div>
      <div className='px-1.5 space-x-1.5 flex flex-1'>
        <span className='flex-1 flex rounded-md'>
          <button
            type='button'
            className='btn-class header-btn relative px-3 py-1.5 block w-full leading-relaxed rounded-md bg-white text-xs 2xl:text-sm tracking-wide font-semibold transition-colors border border-transparent uppercase hover:bg-white-snow focus:bg-primary-50 focus:border-primary focus:ring-1 focus:ring-primary focus:ring-opacity-10 focus:outline-none btn-class '
            data-testid={TEST_IDS.generic.datePicker.toggleMonthSelectorBtn}
            onClick={() => calendar.openMonth()}
          >
            {calendar.month}
          </button>
        </span>
        <span className='flex-1 flex rounded-md'>
          <button
            type='button'
            className='btn-class header-btn relative px-3 py-1.5 block w-full leading-relaxed rounded-md bg-white text-xs 2xl:text-sm tracking-wide font-semibold transition-colors border border-transparent uppercase hover:bg-white-show focus:bg-primary-50 focus:border-primary focus:ring-1 focus:ring-primary focus:ring-opacity-10 focus:outline-none '
            onClick={() => calendar.openYear()}
            data-testid={TEST_IDS.generic.datePicker.toggleYearSelectorBtn}
          >
            {calendar.year}
          </button>
        </span>
      </div>
      <div className='shrink-0 contents'>
        <span
          className={classNames('inline-flex rounded-full', {
            block: panel === PanelView.Calendar || panel === PanelView.Year,
            'visibility-hidden': panel === PanelView.Month || !showRightChevron,
          })}
        >
          <button
            type='button'
            className='btn-class relative p-1.5 rounded-full bg-white transition-colors border border-transparent hover:bg-white-snow focus:bg-primary-50 focus:border-primary  focus:ring-1 focus:ring-primary focus:ring-opacity-10 focus:outline-none '
            onClick={() => (panel === PanelView.Calendar ? calendar.onNext() : calendar.onNextYear())}
            data-testid={
              panel === PanelView.Calendar
                ? TEST_IDS.generic.datePicker.nextMonthArrow
                : TEST_IDS.generic.datePicker.nextYearArrow
            }
          >
            <ArrowButtonIcon orientation='right' doubled={panel === PanelView.Year} />
          </button>
        </span>
      </div>
    </div>
  );
});

type ArrowButtonIconProps = {
  orientation: 'left' | 'right';
  doubled?: boolean;
};

const ArrowButtonIcon = ({ orientation, doubled }: ArrowButtonIconProps) => {
  const path = useMemo(() => {
    if (orientation === 'left') {
      return doubled ? `M11 19l-7-7 7-7m8 14l-7-7 7-7` : `M15 19l-7-7 7-7`;
    } else {
      return doubled ? `M13 5l7 7-7 7M5 5l7 7-7 7` : `M9 5l7 7-7 7`;
    }
  }, [orientation, doubled]);

  return (
    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='1.5' d={path}></path>
    </svg>
  );
};
